import React from 'react';
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import Navigate from '@shared/components/Navigate/navigate';
import { ScrollOnMount } from '@shared/components/ScrollOnMount';
import ShareHotelModal from './ShareHotelModal';
import BoxHotelDetails from './BoxHotelDetails';
import RoomsTable from './RoomsTable';
import HotelDetailSkeleton from './HotelDetailSkeleton';
import NotFound from '@shared/pages/NotFound';
import DialogMapModal from '@shared/components/Dialogs/Map';
import MapContainer from '@shared/components/MapContainer';
import useWindowSize from '@shared/hooks/useWindowsSize';

import useHotelDetail from './hooks/useHotelDetail';
import useAutoTimeout from './hooks/useAutoTimeout';

export function getUrlParams(paramNames) {
  const searchParams = new URLSearchParams(window.location.search);
  return paramNames.reduce((acc, name) => {
    acc[name] = searchParams.get(name);
    return acc;
  }, {});
}

export const HotelDetailStyled = () => {
  const screenSize = useWindowSize();
  const { scrollY, open, handleContinue, handleScrollToTop } = useAutoTimeout();

  const {
    token: urlToken,
    integration: urlIntegrationId,
    hotel: urlHotelId,
    hotel_code: urlHotelCode,
    city: urlIdCity,
    id_provider: urlIdProvider,
  } = getUrlParams([
    'token',
    'integration',
    'hotel',
    'hotel_code',
    'city',
    'id_provider',
  ]);

  const { hotelDetail, searchFilters, isLoading, hasErrors } = useHotelDetail({
    urlToken,
    urlIntegrationId,
    urlHotelId,
    urlHotelCode,
    urlIdCity,
    urlIdProvider,
  });

  const [openShareModal, setOpenShareModal] = React.useState(false);
  const [showMapModal, setShowMapModal] = React.useState(false);
  const sectionRef = React.useRef(null);

  if (hasErrors) return <NotFound />;
  if (isLoading || !hotelDetail) return <HotelDetailSkeleton />;

  return (
    <Box
      sx={{
        backgroundColor: '#F3F3F3',
        width: '100%',
        minHeight: '100vh',
      }}
    >
      {screenSize.width < 1280 && scrollY > 1000 && (
        <Grid sx={{ position: 'fixed', right: 1, bottom: 1, zIndex: 1000 }}>
          <Button
            variant="contained"
            onClick={handleScrollToTop}
            sx={{
              height: 60,
              width: 60,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
            }}
          >
            <ExpandLessIcon />
          </Button>
        </Grid>
      )}

      <Box className="minWebPadding" sx={{ py: 2 }}>
        <Navigate
          steps={[
            { title: 'Alojamiento', navigate: '/hotels/search', data: null },
            { title: 'Detalle de alojamiento', navigate: null, data: null },
          ]}
        />
      </Box>

      <ScrollOnMount />

      <Box className="minWebPadding" sx={{ overflow: 'hidden', pb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BoxHotelDetails
              hotelDetailResult={hotelDetail}
              handleShowModal={() => setShowMapModal(true)}
              search={searchFilters}
              goToTable={() =>
                sectionRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
              }
              handleOpenShareModal={() => setOpenShareModal(true)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              ref={sectionRef}
              sx={{
                borderRadius: 2,
                backgroundColor: '#fff',
                mb: '2vh',
              }}
            >
              <RoomsTable
                rates={hotelDetail?.rate || []}
                id_integration={urlIntegrationId}
                check_in={searchFilters?.checkIn}
                id_hotel={urlHotelId}
                total_passangers={searchFilters?.totalPassengers}
                total_nights={searchFilters?.totalNights}
                id_provider={urlIdProvider}
                data={hotelDetail}
                urlToken={urlToken}
                hotelCode={urlHotelCode}
                idCity={urlIdCity}
                country={searchFilters?.city?.country}
                supplier_code={hotelDetail?.supplier_code}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <ShareHotelModal
        openShareModal={openShareModal}
        setOpenShareModal={setOpenShareModal}
      />

      {hotelDetail.location?.latitude != null && (
        <DialogMapModal
          isOpen={showMapModal}
          onClose={() => setShowMapModal(false)}
          title={hotelDetail.name}
        >
          {showMapModal && (
            <MapContainer
              center={{
                lat: Number(hotelDetail.location.latitude),
                lng: Number(hotelDetail.location.longitude),
              }}
            />
          )}
        </DialogMapModal>
      )}

      <Dialog open={open} onClose={handleContinue}>
        <DialogTitle>¿Necesitás más tiempo para hacer tu reserva?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            No hemos detectado ningún avance en los últimos 20 minutos. Haz clic
            en Continuar para seguir usando la página.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleContinue}
            variant="contained"
            sx={{ width: '30%', height: 40 }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HotelDetailStyled;
